import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Avatar,
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Popover,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import BusinessIcon from '@mui/icons-material/Business';
import InfoIcon from '@mui/icons-material/Info';
import PaymentsIcon from '@mui/icons-material/Payments';
import StarIcon from '@mui/icons-material/Star';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import DoneAllIcon from '@mui/icons-material/DoneAll';

// "Just now", "5 minutes ago", "2 hours ago", "3 days ago"
const timeAgo = (date) => {
  const diff = Date.now() - new Date(date).getTime();
  if (Number.isNaN(diff)) return '';
  const sec = Math.max(0, Math.floor(diff / 1000));
  if (sec < 60) return 'Just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} minute${min > 1 ? 's' : ''} ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr > 1 ? 's' : ''} ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day} day${day > 1 ? 's' : ''} ago`;
  const month = Math.floor(day / 30);
  if (month < 12) return `${month} month${month > 1 ? 's' : ''} ago`;
  return `${Math.floor(day / 365)} year${Math.floor(day / 365) > 1 ? 's' : ''} ago`;
};

// Icon + colour per notification type
const getVisual = (n) => {
  const rejected = /reject|cancel|fail/i.test(n.title || '');
  if (n.type === 'booking') {
    return rejected
      ? { Icon: CancelIcon, color: '#DC2626', bg: '#FEE2E2' }
      : { Icon: EventAvailableIcon, color: '#2563EB', bg: '#DBEAFE' };
  }
  if (n.type === 'approval') {
    return rejected
      ? { Icon: CancelIcon, color: '#DC2626', bg: '#FEE2E2' }
      : { Icon: CheckCircleIcon, color: '#16A34A', bg: '#DCFCE7' };
  }
  if (n.type === 'payment') {
    return rejected
      ? { Icon: CancelIcon, color: '#DC2626', bg: '#FEE2E2' }
      : { Icon: PaymentsIcon, color: '#16A34A', bg: '#DCFCE7' };
  }
  if (n.type === 'review') return { Icon: StarIcon, color: '#CA8A04', bg: '#FEF3C7' };
  if (n.type === 'message') return { Icon: ChatBubbleIcon, color: '#2563EB', bg: '#DBEAFE' };
  if (n.type === 'system') return { Icon: BusinessIcon, color: '#CA8A04', bg: '#FEF3C7' };
  return { Icon: InfoIcon, color: '#4B5563', bg: '#F3F4F6' };
};

/**
 * Facebook-style notification bell.
 *
 * Props
 *  - baseUrl:      API base, e.g. KeyUri.BACKEND_URI
 *  - tokenKey:     localStorage key holding the JWT (default 'vendorToken')
 *  - pollMs:       how often to check for new notifications (default 30s)
 *  - onItemClick:  (notification) => void, called after it is marked read
 */
export function NotificationBell({ baseUrl, tokenKey = 'vendorToken', pollMs = 30000, onItemClick }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('all');
  const open = Boolean(anchorEl);

  // Read the token at call time so a fresh login is picked up
  const authConfig = useCallback(
    () => ({ headers: { Authorization: `Bearer ${localStorage.getItem(tokenKey)}` } }),
    [tokenKey]
  );

  const load = useCallback(
    async (showSpinner = false) => {
      if (!localStorage.getItem(tokenKey)) return;
      if (showSpinner) setLoading(true);
      try {
        const res = await axios.get(`${baseUrl}/vendor/notifications`, {
          params: { limit: 30 },
          ...authConfig(),
        });
        setItems(res.data.data || []);
        setUnreadCount(res.data.unreadCount || 0);
      } catch (err) {
        console.error('Failed to load notifications:', err);
      } finally {
        if (showSpinner) setLoading(false);
      }
    },
    [baseUrl, tokenKey, authConfig]
  );

  // Initial load + polling (paused while the tab is hidden)
  useEffect(() => {
    load(true);
    const tick = () => {
      if (document.visibilityState === 'visible') load(false);
    };
    const id = setInterval(tick, pollMs);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [load, pollMs]);

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
    load(false); // refresh right when opened
  };
  const handleClose = () => setAnchorEl(null);

  const markRead = async (n) => {
    if (n.isRead) return;
    // optimistic update
    setItems((prev) => prev.map((x) => (x._id === n._id ? { ...x, isRead: true } : x)));
    setUnreadCount((c) => Math.max(0, c - 1));
    try {
      await axios.patch(`${baseUrl}/vendor/notifications/${n._id}/read`, null, authConfig());
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
      load(false); // roll back to server state
    }
  };

  const markAllRead = async () => {
    if (unreadCount === 0) return;
    setItems((prev) => prev.map((x) => ({ ...x, isRead: true })));
    setUnreadCount(0);
    try {
      await axios.patch(`${baseUrl}/vendor/notifications/read-all`, null, authConfig());
    } catch (err) {
      console.error('Failed to mark all as read:', err);
      load(false);
    }
  };

  const handleItemClick = async (n) => {
    await markRead(n);
    handleClose();
    if (onItemClick) onItemClick(n);
  };

  const visible = tab === 'unread' ? items.filter((n) => !n.isRead) : items;

  return (
    <>
      <IconButton
        color="inherit"
        aria-label={`notifications${unreadCount ? `, ${unreadCount} unread` : ''}`}
        size="large"
        onClick={handleOpen}
      >
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            width: { xs: 'calc(100vw - 24px)', sm: 380 },
            maxWidth: 420,
            mt: 1,
            borderRadius: 2,
            overflow: 'hidden',
          },
        }}
      >
        {/* Header */}
        <Box sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" fontWeight={700}>
            Notifications
          </Typography>
          <Button size="small" startIcon={<DoneAllIcon />} onClick={markAllRead} disabled={unreadCount === 0}>
            Mark all as read
          </Button>
        </Box>

        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          sx={{ px: 1, minHeight: 40, '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 600 } }}
        >
          <Tab label="All" value="all" />
          <Tab label={`Unread${unreadCount ? ` (${unreadCount})` : ''}`} value="unread" />
        </Tabs>
        <Divider />

        {/* List */}
        <Box sx={{ maxHeight: 440, overflowY: 'auto' }}>
          {loading && items.length === 0 ? (
            <Box sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={28} />
            </Box>
          ) : visible.length === 0 ? (
            <Box sx={{ py: 6, px: 2, textAlign: 'center', color: 'text.secondary' }}>
              <NotificationsNoneIcon sx={{ fontSize: 44, mb: 1, opacity: 0.6 }} />
              <Typography variant="body2">
                {tab === 'unread' ? "You're all caught up." : 'No notifications yet.'}
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {visible.map((n) => {
                const { Icon, color, bg } = getVisual(n);
                return (
                  <ListItemButton
                    key={n._id}
                    onClick={() => handleItemClick(n)}
                    alignItems="flex-start"
                    sx={{
                      gap: 1,
                      py: 1.25,
                      bgcolor: n.isRead ? 'transparent' : 'rgba(37, 99, 235, 0.07)',
                    }}
                  >
                    <ListItemAvatar sx={{ minWidth: 0, mt: 0.25 }}>
                      <Avatar sx={{ bgcolor: bg, width: 40, height: 40 }}>
                        <Icon sx={{ color, fontSize: 22 }} />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      sx={{ m: 0, minWidth: 0 }}
                      primary={
                        <Typography variant="body2" fontWeight={n.isRead ? 500 : 700} sx={{ wordBreak: 'break-word' }}>
                          {n.title}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.secondary"
                            sx={{ display: 'block', wordBreak: 'break-word' }}
                          >
                            {n.message}
                          </Typography>
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{ color: n.isRead ? 'text.secondary' : 'primary.main', fontWeight: n.isRead ? 400 : 600 }}
                          >
                            {timeAgo(n.createdAt)}
                          </Typography>
                        </>
                      }
                    />
                    {!n.isRead && (
                      <Box
                        sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'primary.main', flexShrink: 0, mt: 1.5 }}
                      />
                    )}
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </Box>
      </Popover>
    </>
  );
}

export default NotificationBell;